import React, { useState } from 'react';
import { resolveMediaUrl } from '../services/productsApi';

const buildAssignmentMap = (assignments) => {
  const map = new Map();
  (assignments || []).forEach((assignment) => {
    const attributeId = Number(assignment.attributeDefinitionId);
    if (!Number.isFinite(attributeId)) return;

    const options = Array.isArray(assignment.options)
      ? assignment.options
      : (assignment.optionDefinitionIds || []).map((optionDefinitionId) => ({
          optionDefinitionId,
          imagePath: '',
        }));

    map.set(
      attributeId,
      options
        .map((option) => ({
          optionDefinitionId: Number(option.optionDefinitionId),
          imagePath: option.imagePath || '',
        }))
        .filter((option) => Number.isFinite(option.optionDefinitionId))
    );
  });
  return map;
};

const assignmentsFromMap = (map) =>
  Array.from(map.entries()).map(([attributeDefinitionId, options]) => ({
    attributeDefinitionId,
    options,
  }));

const AdminProductAttributesSection = ({
  attributeDefinitions,
  assignments,
  onChange,
  onUploadOptionImage,
  disabled = false,
}) => {
  const [uploadingKey, setUploadingKey] = useState(null);
  const [expandedFinishIds, setExpandedFinishIds] = useState(() => new Set());

  const selectedMap = buildAssignmentMap(assignments);

  const finishDefinitions = (attributeDefinitions || []).filter(
    (attribute) => attribute.attribute_type === 'image'
  );

  const selectedFinishCount = finishDefinitions.filter((attribute) =>
    selectedMap.has(Number(attribute.id))
  ).length;

  const allFinishesSelected =
    finishDefinitions.length > 0 &&
    selectedFinishCount === finishDefinitions.length;

  const emitChange = (nextMap) => {
    onChange(assignmentsFromMap(nextMap));
  };

  const buildFinishOptions = (attribute) =>
    (attribute.options || [])
      .map((option) => ({
        optionDefinitionId: Number(option.id),
        imagePath: '',
      }))
      .filter((option) => Number.isFinite(option.optionDefinitionId));

  const selectAllFinishes = () => {
    const nextMap = new Map(selectedMap);
    finishDefinitions.forEach((attribute) => {
      nextMap.set(Number(attribute.id), buildFinishOptions(attribute));
    });
    setExpandedFinishIds(new Set());
    emitChange(nextMap);
  };

  const clearAllFinishes = () => {
    const nextMap = new Map(selectedMap);
    finishDefinitions.forEach((attribute) => {
      nextMap.delete(Number(attribute.id));
    });
    setExpandedFinishIds(new Set());
    emitChange(nextMap);
  };

  const toggleFinishExpanded = (attributeId) => {
    setExpandedFinishIds((prev) => {
      const next = new Set(prev);
      if (next.has(attributeId)) next.delete(attributeId);
      else next.add(attributeId);
      return next;
    });
  };

  const toggleAttribute = (attribute) => {
    const attributeId = Number(attribute.id);
    const nextMap = new Map(selectedMap);

    if (nextMap.has(attributeId)) {
      nextMap.delete(attributeId);
      setExpandedFinishIds((prev) => {
        const next = new Set(prev);
        next.delete(attributeId);
        return next;
      });
    } else if (attribute.attribute_type === 'image') {
      nextMap.set(attributeId, buildFinishOptions(attribute));
      // Mantener la lista colapsada para no ocupar pantalla
      setExpandedFinishIds((prev) => {
        const next = new Set(prev);
        next.delete(attributeId);
        return next;
      });
    } else {
      nextMap.set(attributeId, []);
    }

    emitChange(nextMap);
  };

  const toggleOption = (attributeId, optionId) => {
    const nextMap = new Map(selectedMap);
    const current = [...(nextMap.get(attributeId) || [])];
    const existingIndex = current.findIndex(
      (item) => item.optionDefinitionId === optionId
    );

    if (existingIndex >= 0) {
      current.splice(existingIndex, 1);
    } else {
      current.push({ optionDefinitionId: optionId, imagePath: '' });
    }

    nextMap.set(attributeId, current);
    emitChange(nextMap);
  };

  const clearOptionImage = (attributeId, optionId) => {
    const nextMap = new Map(selectedMap);
    const current = [...(nextMap.get(attributeId) || [])];
    const index = current.findIndex(
      (item) => item.optionDefinitionId === optionId
    );
    if (index < 0) return;
    current[index] = { ...current[index], imagePath: '' };
    nextMap.set(attributeId, current);
    emitChange(nextMap);
  };

  const handleOptionImageUpload = async (attributeId, optionId, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onUploadOptionImage) return;

    const uploadKey = `${attributeId}-${optionId}`;
    setUploadingKey(uploadKey);
    try {
      const imagePath = await onUploadOptionImage(file);
      const nextMap = new Map(selectedMap);
      const current = [...(nextMap.get(attributeId) || [])];
      const index = current.findIndex(
        (item) => item.optionDefinitionId === optionId
      );
      if (index >= 0) {
        current[index] = { ...current[index], imagePath: imagePath || '' };
        nextMap.set(attributeId, current);
        emitChange(nextMap);
      }
    } finally {
      setUploadingKey(null);
    }
  };

  if (!attributeDefinitions || attributeDefinitions.length === 0) {
    return (
      <div className="admin-product-attributes-section">
        <h3 className="admin-panel-section-title admin-section-subtitle">
          Atributos del producto
        </h3>
        <p className="admin-panel-hint">
          Primero crea atributos en el catálogo de arriba para poder asignarlos.
        </p>
      </div>
    );
  }

  return (
    <div className="admin-product-attributes-section">
      <h3 className="admin-panel-section-title admin-section-subtitle">
        Atributos del producto
      </h3>
      <p className="admin-panel-hint">
        Marca qué atributos aplican a este producto. En atributos tipo{' '}
        <strong>Acabado</strong>, al seleccionarlos se incluyen todos los
        valores automáticamente (la lista queda oculta; puedes expandirla si
        necesitas quitar alguno).
      </p>

      {finishDefinitions.length > 0 && (
        <div className="admin-finish-bulk-actions">
          <span className="admin-finish-bulk-summary">
            Acabados: {selectedFinishCount} de {finishDefinitions.length}{' '}
            seleccionados
          </span>
          <div className="admin-finish-bulk-buttons">
            <button
              type="button"
              className="admin-finish-toggle-btn"
              onClick={selectAllFinishes}
              disabled={disabled || allFinishesSelected}
            >
              Seleccionar todos los acabados
            </button>
            <button
              type="button"
              className="admin-finish-toggle-btn admin-finish-clear-btn"
              onClick={clearAllFinishes}
              disabled={disabled || selectedFinishCount === 0}
            >
              Quitar acabados
            </button>
          </div>
        </div>
      )}

      <div className="admin-product-attribute-assignments">
        {attributeDefinitions.map((attribute) => {
          const options = Array.isArray(attribute.options)
            ? attribute.options
            : [];
          const attributeId = Number(attribute.id);
          const isSelected = selectedMap.has(attributeId);
          const selectedOptions = selectedMap.get(attributeId) || [];
          const isFinish = attribute.attribute_type === 'image';
          const isFinishExpanded = expandedFinishIds.has(attributeId);
          const showOptions = isSelected && (!isFinish || isFinishExpanded);

          return (
            <div className="admin-product-attribute-block" key={attribute.id}>
              <label className="admin-product-attribute-check">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleAttribute(attribute)}
                  disabled={disabled}
                />
                <span>
                  <strong>{attribute.label}</strong>
                  <span className="admin-attribute-meta">
                    {attribute.attribute_type}
                    {isFinish && attribute.grade
                      ? ` · Grado ${attribute.grade}`
                      : ''}
                  </span>
                </span>
              </label>

              {isSelected && isFinish && (
                <div className="admin-finish-assignment-summary">
                  <span>
                    {selectedOptions.length} de {options.length}{' '}
                    {options.length === 1
                      ? 'valor incluido'
                      : 'valores incluidos'}
                  </span>
                  <button
                    type="button"
                    className="admin-finish-toggle-btn"
                    onClick={() => toggleFinishExpanded(attributeId)}
                    disabled={disabled || options.length === 0}
                  >
                    {isFinishExpanded
                      ? 'Ocultar valores'
                      : 'Ver / ajustar valores'}
                  </button>
                </div>
              )}

              {showOptions && (
                <div
                  className={`admin-product-attribute-options ${
                    isFinish ? 'is-finish-compact' : ''
                  }`}
                >
                  {options.length === 0 ? (
                    <p className="admin-panel-hint">
                      Este atributo no tiene valores en el catálogo.
                    </p>
                  ) : (
                    options.map((option) => {
                      const optionId = Number(option.id);
                      const selectedOption = selectedOptions.find(
                        (item) => item.optionDefinitionId === optionId
                      );
                      const isOptionSelected = Boolean(selectedOption);
                      const uploadKey = `${attributeId}-${optionId}`;
                      const isUploading = uploadingKey === uploadKey;

                      return (
                        <div
                          className="admin-product-option-row"
                          key={option.id}
                        >
                          <label className="admin-product-option-check">
                            <input
                              type="checkbox"
                              checked={isOptionSelected}
                              onChange={() =>
                                toggleOption(attributeId, optionId)
                              }
                              disabled={disabled}
                            />
                            {attribute.attribute_type === 'color' &&
                              option.hex_color && (
                                <span
                                  className="admin-attribute-swatch"
                                  style={{
                                    backgroundColor: option.hex_color,
                                  }}
                                />
                              )}
                            {isFinish && option.image_path && (
                              <img
                                src={resolveMediaUrl(option.image_path)}
                                alt=""
                                className="admin-attribute-finish-thumb"
                              />
                            )}
                            <span>{option.label}</span>
                          </label>

                          {isOptionSelected && !isFinish && (
                            <div className="admin-product-option-image">
                              <div className="admin-file-picker">
                                <label
                                  className={`admin-file-btn admin-file-btn-small ${
                                    isUploading || disabled ? 'disabled' : ''
                                  }`}
                                  htmlFor={`admin-option-image-${attributeId}-${optionId}`}
                                >
                                  {isUploading
                                    ? 'Subiendo...'
                                    : 'Imagen (opcional)'}
                                </label>
                                <input
                                  id={`admin-option-image-${attributeId}-${optionId}`}
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) =>
                                    handleOptionImageUpload(
                                      attributeId,
                                      optionId,
                                      event
                                    )
                                  }
                                  disabled={isUploading || disabled}
                                />
                              </div>
                              <span className="admin-file-text">
                                {selectedOption?.imagePath
                                  ? selectedOption.imagePath
                                  : 'Sin imagen'}
                              </span>
                              {selectedOption?.imagePath && (
                                <button
                                  type="button"
                                  className="admin-file-remove-btn"
                                  onClick={() =>
                                    clearOptionImage(attributeId, optionId)
                                  }
                                  disabled={isUploading || disabled}
                                >
                                  Quitar
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminProductAttributesSection;
